import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { XPProvider } from './contexts/XPContext';
import { ToastProvider } from './components/ui/Toast';
import XPNotification from './components/XPNotification';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import GenerateResumes from './pages/GenerateResumes';
import ResumeViewer from './pages/ResumeViewer';
import ManualResumeEditor from './pages/ManualResumeEditor';
import RoleResumeViewer from './pages/RoleResumeViewer';
import DiscoverJobs from './pages/DiscoverJobs';
import InterviewPrep from './pages/InterviewPrep';
import Progress from './pages/Progress';
import QuizPractice from './pages/QuizPractice';
import MockInterviewSetup from './pages/MockInterviewSetup';
import ActiveMockInterview from './pages/ActiveMockInterview';
import MockInterviewResults from './pages/MockInterviewResults';
import MockInterviewHistory from './pages/MockInterviewHistory';
import Profile from './pages/Profile';
import Upgrade from './pages/Upgrade';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import AuthCallback from './pages/AuthCallback';
import OnboardingWizard from './pages/onboarding/OnboardingWizard';
import AdminJobs from './pages/AdminJobs';
import AdminAnalytics from './pages/AdminAnalytics';
import './App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-base)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-primary)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '3px solid var(--border-subtle)',
            borderTop: '3px solid var(--indigo-500)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: 'var(--text-tertiary)' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <XPProvider>
        <SidebarProvider>
          <ToastProvider>
          <Router>
        <Routes>
          {/* Landing page as default homepage */}
          <Route path="/" element={<Landing />} />
          
          {/* Authentication */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Onboarding */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingProvider>
                  <OnboardingWizard />
                </OnboardingProvider>
              </ProtectedRoute>
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

          <Route
            path="/resume/:appId"
            element={
              <ProtectedRoute>
                <ResumeViewer />
              </ProtectedRoute>
            }
          />

          <Route
            path="/manual-resume/:resumeId"
            element={
              <ProtectedRoute>
                <ManualResumeEditor />
              </ProtectedRoute>
            }
          />

          <Route
            path="/role-resume/:role"
            element={
              <ProtectedRoute>
                <RoleResumeViewer />
              </ProtectedRoute>
            }
          />

          <Route
            path="/discover-jobs"
            element={
              <ProtectedRoute>
                <DiscoverJobs />
              </ProtectedRoute>
            }
          />

          <Route
            path="/interview-prep"
            element={
              <ProtectedRoute>
                <InterviewPrep />
              </ProtectedRoute>
            }
          />

          <Route
            path="/generate-resumes"
            element={
              <ProtectedRoute>
                <GenerateResumes />
              </ProtectedRoute>
            }
          />

          <Route
            path="/progress"
            element={
              <ProtectedRoute>
                <Progress />
              </ProtectedRoute>
            }
          />

          <Route
            path="/quiz-practice"
            element={
              <ProtectedRoute>
                <QuizPractice />
              </ProtectedRoute>
            }
          />

          {/* Mock Interview Routes */}
          <Route
            path="/mock-interview/setup"
            element={
              <ProtectedRoute>
                <MockInterviewSetup />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mock-interview/:interviewId"
            element={
              <ProtectedRoute>
                <ActiveMockInterview />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mock-interview/:interviewId/results"
            element={
              <ProtectedRoute>
                <MockInterviewResults />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mock-interview/history"
            element={
              <ProtectedRoute>
                <MockInterviewHistory />
              </ProtectedRoute>
            }
          />

          {/* Catch all - redirect to landing */}
          {/* Payment & Subscription routes */}
          <Route
            path="/upgrade"
            element={
              <ProtectedRoute>
                <Upgrade />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment/success"
            element={
              <ProtectedRoute>
                <PaymentSuccess />
              </ProtectedRoute>
            }
          />

          <Route
            path="/payment/cancel"
            element={
              <ProtectedRoute>
                <PaymentCancel />
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/jobs"
            element={
              <ProtectedRoute>
                <AdminJobs />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <AdminAnalytics />
              </ProtectedRoute>
            }
          />

          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </Router>
          </ToastProvider>
        </SidebarProvider>
        <XPNotification />
        </XPProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;